import { hasErrorBoundary } from '@xmanscript/has-error-boundary';
import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';

import { AuthenticatedLinkService, ProductService, UserService } from '@/ui/_apiUrls';
import { ReactComponent as Business } from '@/assets/image/Business.svg';
import Button from '../Button';
import { AnalyticsPage } from '@/ui/pages/analytics';
import { useUser } from '@/contexts/UserContext/UserContext';
import { authenticatedApi } from '@/api/config';


function getDefaultLinkIcon() {
  return (
    <div className="flex h-[2.5rem] w-[2.5rem] flex-shrink-0 items-center justify-center rounded-full bg-[#039BE5]">
      <svg className="h-4 w-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
      </svg>
    </div>
  );
}

function getLinkThumbnail(_url: string, title: string, thumbnail?: string) {
  // If thumbnail exists, use it
  if (thumbnail && (thumbnail.startsWith('http://') || thumbnail.startsWith('https://'))) {
    return (
      <img 
        src={thumbnail} 
        alt={title || 'Link thumbnail'} 
        className="h-[2.5rem] w-[2.5rem] flex-shrink-0 rounded-full object-cover"
        onError={(e) => {
          // Fallback to default link icon if image fails to load
          const target = e.target as HTMLImageElement;
          target.onerror = null;
          const parent = target.parentElement;
          if (parent) {
            parent.innerHTML = '';
            parent.appendChild(getDefaultLinkIcon() as any);
          }
        }}
      />
    );
  }
  
  // For products or links without thumbnails, use default link icon
  return getDefaultLinkIcon();
}

function getActualClicks(link: any) {
  // Use actual click count from backend if available, otherwise default to 0
  return link.click_count !== undefined ? link.click_count : 0;
}

function AnalyticsTab() {
  const [showAdvancedAnalytics, setShowAdvancedAnalytics] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const { user, refreshUser, updateUser } = useUser(); // Also get refreshUser and updateUser functions
  const queryClient = useQueryClient();

  const { data: links } = AuthenticatedLinkService.get({ queryParams: { queryKey: ['link'] } });
  const { data: products } = ProductService.get({ queryParams: { queryKey: ['products'] } });

  // Function to refresh only analytics data
  const refreshAnalyticsData = async () => {
    setIsRefreshing(true);
    try {
      // Refresh the specific queries we need for analytics
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['link'] }),
        queryClient.invalidateQueries({ queryKey: ['products'] })
      ]);
      
      // Fetch user data directly to get updated click counts without affecting context state flow
      // This avoids triggering the redirection logic in the parent Links component
      const response = await authenticatedApi.get('/api/v1/user/');
      if (response && response.data) {
        // Update the user context with fresh data directly to update click counts
        // but do it in a way that maintains the current user profile status
        const userData = {
          id: response.data.id || response.data._id,
          name: response.data.name || '',
          email: response.data.email || '',
          thumbnail: response.data.thumbnail || '',
          title: response.data.title,
          profileComplete: response.data.profileComplete || response.data.profile_complete || false,
          categories: response.data.categories || [],
          showProfileName: response.data.showProfileName || response.data.show_profile_name,
          showProfileImage: response.data.showProfileImage || response.data.show_profile_image,
          gender: response.data.gender,
          bio: response.data.bio,
          template: response.data.template || null,
          highlight_title: response.data.highlight_title,
          location_title: response.data.location_title,
          location_url: response.data.location_url,
          google_review_url: response.data.google_review_url,
          location_click_count: response.data.location_click_count,
          google_review_click_count: response.data.google_review_click_count,
        };
        // Update user context without triggering full refresh logic
        updateUser(userData);
      }
    } catch (error) {
      console.error("Error refreshing analytics data:", error);
      // If API call fails, still refresh the queries to potentially get cached data
      queryClient.invalidateQueries({ queryKey: ['link'] });
      queryClient.invalidateQueries({ queryKey: ['products'] });
    } finally {
      setIsRefreshing(false);
    }
  };

  // If advanced analytics is requested, show the new analytics page
  if (showAdvancedAnalytics) {
    return (
      <div className="h-full w-full">
        <AnalyticsPage onBack={() => setShowAdvancedAnalytics(false)} />
      </div>
    );
  }

  if (
    (!links?.result?.length || links.result.length === 0) &&
    (!products?.result?.length || products.result.length === 0) &&
    (!user?.location_url) &&
    (!user?.google_review_url)
  ) {
    return (
      <div className="flex h-full w-full flex-col items-center justify-center px-1">
        <div className="mt-8 flex w-full items-center justify-center">
          <Business />
        </div>
        <div className="mt-6 text-center">
          <h3 className="mb-2 text-lg font-semibold text-gray-900">No Analytics Data</h3>
          <p className="text-sm text-gray-600">Add some links or highlights to start seeing analytics.</p>
        </div>
      </div>
    );
  }

  const allLinks = [...(links?.result || []), ...(products?.result || [])];

  // Filter out Google Review links to avoid duplicate display
  const filteredLinks = allLinks.filter((link: any) => {
    // Check if this is a Google Review link by name
    const isGoogleReviewByName = link.name && link.name.toLowerCase() === 'google review';
    // Check if this is a Google Review link by URL pattern (if applicable)
    const isGoogleReviewByUrl = link.url && link.url.toLowerCase().includes('google') && link.url.toLowerCase().includes('review');
    
    // Exclude Google Review links from the main list since they're displayed separately
    return !isGoogleReviewByName && !isGoogleReviewByUrl;
  });

  return (
    <div className="flex h-full w-full flex-col justify-between pt-1">
      <div className="flex flex-col gap-2 flex-1">
        <div className="small-scrollbar flex h-full flex-col gap-2 overflow-y-auto">
          {/* Links List */}
          {filteredLinks.map((link: any) => (
            <div
              key={link.id || link._id || `link-${Math.random()}` /* eslint-disable-line no-underscore-dangle */}
              className="flex w-full flex-col gap-2 rounded-lg bg-white shadow-[0px_1px_2px_0px_#47546733]"
              role="article"
            >
              <div className="flex items-start justify-between p-2 sm:w-full md:p-4">
                <div className="flex flex-1 items-center gap-3">
                  <div className="w-[2.5rem] flex-shrink-0">
                    {getLinkThumbnail(link.url || link.link || '', link.title || link.name || '', link.thumbnail)}
                  </div>
                  <div className="ml-3 flex w-[8rem] flex-1 flex-col gap-1 overflow-hidden sm:flex-1">
                    <p
                      title={link.title || link.name || 'Untitled Link'}
                      className="truncate break-words font-['Inter'] text-[13px] font-bold leading-[17px] text-[#475467]"
                    >
                      {link.title || link.name || 'Untitled Link'}
                    </p>
                    <a
                      className="w-full truncate break-words text-xs text-[#667085]"
                      style={{ wordWrap: 'break-word' }}
                      title={link.url || link.link || 'No URL'}
                      href={link.url || link.link || '#'}
                    >
                      {link.url || link.link || 'No URL'}
                    </a>
                  </div>
                </div>
                <div className="flex flex-shrink-0 items-center gap-2">
                  <div className="h-8 w-px bg-[#D7D7D7]" />
                  <div className="flex flex-col items-center justify-center text-right">
                    <div className="text-[16px] font-bold leading-[16px] text-[#039BE5]">{getActualClicks(link)}</div>
                    <div className="text-[12px] font-normal leading-[17px] text-gray-500">Clicks</div>
                  </div>
                </div>
              </div>
            </div>
          ))}
          
          {/* Location Clicks */}
          {user?.location_url && (
            <div className="flex w-full flex-col gap-2 rounded-lg bg-white shadow-[0px_1px_2px_0px_#47546733]">
              <div className="flex items-start justify-between p-2 sm:w-full md:p-4">
                <div className="flex flex-1 items-center gap-3">
                  <div className="w-[2.5rem] flex-shrink-0">
                    <div className="flex h-[2.5rem] w-[2.5rem] flex-shrink-0 items-center justify-center rounded-full bg-[#EA4335]">
                      <svg viewBox="0 0 24 24" className="h-4 w-4 fill-white">
                        <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z" />
                      </svg>
                    </div>
                  </div>
                  <div className="ml-3 flex w-[8rem] flex-1 flex-col gap-1 overflow-hidden sm:flex-1">
                    <p className="truncate break-words font-['Inter'] text-[13px] font-bold leading-[17px] text-[#475467]">
                      Location Clicks
                    </p>
                    <p className="w-full truncate break-words text-xs text-[#667085]">
                      Map and location links
                    </p>
                  </div>
                </div>
                <div className="flex flex-shrink-0 items-center gap-2">
                  <div className="h-8 w-px bg-[#D7D7D7]"/>
                  <div className="flex flex-col items-center justify-center text-right">
                    <div className="text-[16px] font-bold leading-[16px] text-[#039BE5]">{user?.location_click_count ?? 0}</div>
                    <div className="text-[12px] font-normal leading-[17px] text-gray-500">Clicks</div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Google Review Clicks */}
          {user?.google_review_url && (
            <div className="flex w-full flex-col gap-2 rounded-lg bg-white shadow-[0px_1px_2px_0px_#47546733]">
              <div className="flex items-start justify-between p-2 sm:w-full md:p-4">
                <div className="flex flex-1 items-center gap-3">
                  <div className="w-[2.5rem] flex-shrink-0">
                    <div className="flex h-[2.5rem] w-[2.5rem] flex-shrink-0 items-center justify-center rounded-full bg-[#4285F4]">
                      <svg viewBox="0 0 24 24" className="h-4 w-4 fill-white">
                        <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                        <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                        <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                        <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                      </svg>
                    </div>
                  </div>
                  <div className="ml-3 flex w-[8rem] flex-1 flex-col gap-1 overflow-hidden sm:flex-1">
                    <p className="truncate break-words font-['Inter'] text-[13px] font-bold leading-[17px] text-[#475467]">
                      Google Review Clicks
                    </p>
                    <p className="w-full truncate break-words text-xs text-[#667085]">
                      Review link clicks
                    </p>
                  </div>
                </div>
                <div className="flex flex-shrink-0 items-center gap-2">
                  <div className="h-8 w-px bg-[#D7D7D7]"/>
                  <div className="flex flex-col items-center justify-center text-right">
                    <div className="text-[16px] font-bold leading-[16px] text-[#039BE5]">{user?.google_review_click_count ?? 0}</div>
                    <div className="text-[12px] font-normal leading-[17px] text-gray-500">Clicks</div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Refresh and Advanced Analytics Buttons */}
      <div className="mt-4 flex h-fit w-full items-center justify-center gap-2">
        <button 
          type="button" 
          className={`relative overflow-hidden flex items-center justify-center ${isRefreshing ? 'w-12 h-12 rounded-full animate-pulse bg-[#ECF3FA] transition-all duration-200 ease-in-out' : 'font-medium'}`}
          onClick={refreshAnalyticsData}
          disabled={isRefreshing}
          style={isRefreshing ? { width: '48px', height: '48px' } : {}}
        >
          <svg 
            width="48" 
            height="48" 
            viewBox="0 0 48 48" 
            fill="none" 
            xmlns="http://www.w3.org/2000/svg"
            className={`${isRefreshing ? 'animate-spin' : ''}`}
            style={{ 
              width: isRefreshing ? '24px' : '48px', 
              height: isRefreshing ? '24px' : '48px'
            }}
          >
<rect width="48" height="48" rx="8" fill="#ECF3FA"/>
<path d="M19 24.8203C19 27.3203 20.7969 29.3516 23.1797 29.7422V31.4609C19.8594 31.0312 17.3203 28.2188 17.3203 24.8203C17.3203 22.9844 18.1016 21.3438 19.2734 20.1328L20.4844 21.3047C19.5469 22.2031 19 23.4531 19 24.8203ZM30.6797 24.8203C30.6797 28.2188 28.1406 31.0312 24.8203 31.4609V29.7422C27.2031 29.3516 29 27.3203 29 24.8203C29 22.0859 26.7734 19.8203 24 19.8203C23.9609 19.8203 23.9219 19.8594 23.8438 19.8594L24.7422 20.7578L23.5703 21.9297L20.6797 19L23.5703 16.0703L24.7422 17.2812L23.8438 18.1797C23.8828 18.1797 23.9609 18.1797 24 18.1797C27.6719 18.1797 30.6797 21.1484 30.6797 24.8203Z" fill="#039BE5"/>
</svg>

        </button>
        <Button className="w-full font-medium" onClick={() => setShowAdvancedAnalytics(true)}>
          <p className="font-roboto-medium text-[14px]">Advanced Analytics</p>
        </Button>
      </div>
    </div>
  );
}

export default hasErrorBoundary(AnalyticsTab);